import { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
    Shield, ShieldCheck, ShieldOff, KeyRound, Loader2,
    CheckCircle, Copy, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

export default function AdminSecurityPage() {
    const { admin, apiCall } = useAdminAuth();
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [setupData, setSetupData] = useState(null);
    const [verifyCode, setVerifyCode] = useState('');
    const [disableCode, setDisableCode] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('idle'); // idle | setup | verify | disable

    useEffect(() => {
        setIs2FAEnabled(admin?.totp_enabled || false);
    }, [admin]);

    const handleSetup2FA = async () => {
        setLoading(true);
        try {
            const res = await apiCall('post', '/admin/auth/setup-2fa');
            setSetupData(res.data);
            setStep('verify');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al configurar 2FA');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyAndEnable = async (e) => {
        e.preventDefault();
        if (verifyCode.length < 6) return;
        setLoading(true);
        try {
            await apiCall('post', '/admin/auth/enable-2fa', { code: verifyCode });
            setIs2FAEnabled(true);
            setStep('idle');
            setSetupData(null);
            setVerifyCode('');
            toast.success('2FA activado exitosamente');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Codigo invalido');
            setVerifyCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleDisable = async (e) => {
        e.preventDefault();
        if (disableCode.length < 6) return;
        setLoading(true);
        try {
            await apiCall('post', '/admin/auth/disable-2fa', { code: disableCode });
            setIs2FAEnabled(false);
            setStep('idle');
            setDisableCode('');
            toast.success('2FA desactivado');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Codigo invalido');
            setDisableCode('');
        } finally {
            setLoading(false);
        }
    };

    const copySecret = () => {
        if (setupData?.secret) {
            navigator.clipboard.writeText(setupData.secret);
            toast.success('Secreto copiado al portapapeles');
        }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="admin-security-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="security-title">
                    Seguridad
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Configura la autenticacion de dos factores para proteger tu cuenta.</p>
            </div>

            {/* 2FA Status Card */}
            <Card className="card-aviation overflow-hidden" data-testid="2fa-status-card">
                <div className={`h-1 ${is2FAEnabled ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${is2FAEnabled ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                                {is2FAEnabled ? <ShieldCheck className="w-6 h-6 text-emerald-500" /> : <Shield className="w-6 h-6 text-amber-500" />}
                            </div>
                            <div>
                                <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide">
                                    Autenticacion de Dos Factores (2FA)
                                </CardTitle>
                                <CardDescription className="mt-0.5">
                                    Codigo temporal basado en tiempo (TOTP)
                                </CardDescription>
                            </div>
                        </div>
                        <Badge
                            className={is2FAEnabled ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/20'}
                            data-testid="2fa-status-badge"
                        >
                            {is2FAEnabled ? 'Activo' : 'Inactivo'}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Idle state - show enable/disable buttons */}
                    {step === 'idle' && (
                        <div>
                            {!is2FAEnabled ? (
                                <div className="space-y-4">
                                    <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-bold text-amber-500">Recomendado</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    2FA agrega una capa adicional de seguridad a tu cuenta. Necesitaras una app como
                                                    <strong className="text-foreground"> Google Authenticator</strong> o
                                                    <strong className="text-foreground"> Authy</strong>.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <Button
                                        onClick={handleSetup2FA}
                                        disabled={loading}
                                        className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs"
                                        data-testid="2fa-enable-btn"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                                        Activar 2FA
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                        <div className="flex items-center gap-3">
                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                            <p className="text-sm text-muted-foreground">
                                                Tu cuenta esta protegida con autenticacion de dos factores.
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep('disable')}
                                        className="text-destructive border-destructive/30 hover:bg-destructive/10 font-bold uppercase tracking-wider text-xs"
                                        data-testid="2fa-disable-start-btn"
                                    >
                                        <ShieldOff className="w-4 h-4 mr-2" /> Desactivar 2FA
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Setup / Verify step */}
                    {step === 'verify' && setupData && (
                        <div className="space-y-6" data-testid="2fa-setup-section">
                            <div className="text-center">
                                <p className="text-sm text-muted-foreground mb-4">
                                    Escanea el codigo QR con tu aplicacion de autenticacion:
                                </p>
                                <div className="inline-block p-4 bg-white rounded-xl" data-testid="2fa-qr-code">
                                    <QRCodeSVG value={setupData.uri} size={200} />
                                </div>
                            </div>

                            {/* Manual entry */}
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">O ingresa el secreto manualmente:</Label>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 relative">
                                        <Input
                                            readOnly
                                            value={showSecret ? setupData.secret : '****************************'}
                                            className="input-aviation font-mono text-xs pr-20"
                                            data-testid="2fa-secret-input"
                                        />
                                        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                            <button
                                                onClick={() => setShowSecret(!showSecret)}
                                                className="p-1.5 rounded hover:bg-muted transition-colors"
                                                type="button"
                                            >
                                                {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                            </button>
                                            <button
                                                onClick={copySecret}
                                                className="p-1.5 rounded hover:bg-muted transition-colors"
                                                type="button"
                                                data-testid="2fa-copy-secret"
                                            >
                                                <Copy className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Verify code */}
                            <form onSubmit={handleVerifyAndEnable} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Ingresa el codigo generado por tu app:</Label>
                                    <Input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder="000000"
                                        value={verifyCode}
                                        onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required
                                        autoFocus
                                        data-testid="2fa-verify-code-input"
                                        className="input-aviation text-center text-xl font-mono tracking-[0.4em] py-3"
                                    />
                                </div>
                                <div className="flex gap-3">
                                    <Button
                                        type="submit"
                                        disabled={loading || verifyCode.length < 6}
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 font-bold uppercase tracking-wider text-xs"
                                        data-testid="2fa-confirm-enable-btn"
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                        Verificar y Activar
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => { setStep('idle'); setSetupData(null); setVerifyCode(''); }}
                                        className="text-xs"
                                        data-testid="2fa-cancel-setup-btn"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Disable step */}
                    {step === 'disable' && (
                        <form onSubmit={handleDisable} className="space-y-4" data-testid="2fa-disable-section">
                            <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
                                <p className="text-sm text-muted-foreground">
                                    Ingresa tu codigo 2FA actual para confirmar la desactivacion.
                                    <strong className="text-destructive"> Esta accion reducira la seguridad de tu cuenta.</strong>
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>Codigo de verificacion</Label>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="000000"
                                    value={disableCode}
                                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    required
                                    autoFocus
                                    data-testid="2fa-disable-code-input"
                                    className="input-aviation text-center text-xl font-mono tracking-[0.4em] py-3"
                                />
                            </div>
                            <div className="flex gap-3">
                                <Button
                                    type="submit"
                                    variant="destructive"
                                    disabled={loading || disableCode.length < 6}
                                    className="flex-1 font-bold uppercase tracking-wider text-xs"
                                    data-testid="2fa-confirm-disable-btn"
                                >
                                    {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                    Desactivar 2FA
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => { setStep('idle'); setDisableCode(''); }}
                                    className="text-xs"
                                    data-testid="2fa-cancel-disable-btn"
                                >
                                    Cancelar
                                </Button>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* Account Info */}
            <Card className="card-aviation" data-testid="account-info-card">
                <CardHeader>
                    <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                        <Shield className="w-4 h-4 text-primary" /> Informacion de Cuenta
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Email</p>
                            <p className="text-sm font-mono">{admin?.email}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Rol</p>
                            <p className="text-sm capitalize">{admin?.role}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Nombre</p>
                            <p className="text-sm">{admin?.first_name} {admin?.last_name}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">2FA</p>
                            <Badge className={is2FAEnabled ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/20'}>
                                {is2FAEnabled ? 'Activo' : 'Inactivo'}
                            </Badge>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Auditoria 4 - Item 1 (Mock): Enlaces de recuperacion pendientes.
                Cuando exista proveedor de email real este panel se puede ocultar. */}
            <PasswordResetsPanel apiCall={apiCall} />
        </div>
    );
}

function PasswordResetsPanel({ apiCall }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await apiCall('GET', '/admin/password-resets');
            const data = res?.data ?? res;
            setItems(Array.isArray(data) ? data : []);
        } catch { /* silent */ }
        setLoading(false);
    };

    useEffect(() => { load(); }, []); // eslint-disable-line

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-amber-500" />
                            Enlaces de Recuperación Pendientes
                        </CardTitle>
                        <CardDescription>
                            Los enlaces se envían por correo al usuario. Aquí solo ves quién tiene una solicitud activa; el enlace nunca se muestra.
                        </CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-password-resets">
                        {loading && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                        Refrescar
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {items.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="password-resets-empty">No hay solicitudes pendientes.</p>
                ) : (
                    <div className="space-y-2" data-testid="password-resets-list">
                        {items.map(item => (
                            <div key={item.id} className="p-3 rounded-lg border border-border/50 bg-muted/20" data-testid={`password-reset-${item.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold">{item.email}</span>
                                            <Badge variant="outline" className="text-[10px]">{item.scope}</Badge>
                                            {item.expired && <Badge className="text-[10px] bg-red-500/10 text-red-500 border-red-500/40">Expirado</Badge>}
                                        </div>
                                        {item.user_name && <p className="text-xs text-muted-foreground">{item.user_name}</p>}
                                        <p className="text-[10px] text-muted-foreground mt-1">Creado: {new Date(item.created_at).toLocaleString('es-ES')} · {item.purpose === 'activation' ? 'Activación' : 'Restablecer'}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
